import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Button,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Container
} from "@mui/material";

import StorefrontIcon from "@mui/icons-material/Storefront";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LogoutIcon from "@mui/icons-material/Logout";

import { api } from "../api/api";
import { useAuth } from "../context/AuthContext";

export default function Account() {
  const { vendor, logout } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  const [logoutDialog, setLogoutDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);

  // 👈 ADDED: Dedicated loading states for dialog buttons
  const [dialogLoading, setDialogLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  // 🔥 FETCH PROFILE
  const fetchVendor = useCallback(async () => {
    try {
      const res = await api.get(`/vendor/${vendor?.id}`);
      setProfile(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [vendor?.id]);

  useEffect(() => {
    if (vendor?.id) {
      fetchVendor();
    }
  }, [vendor?.id, fetchVendor]);

  // 🔥 TOGGLE SHOP (Updated to show proper loading spinner)
  const toggleShop = async () => {
    try {
      setDialogLoading(true); // 👈 Show spinner on button
      setUpdating(true); // 👈 Disable switch in background

      const res = await api.put(`/vendor/${vendor.id}/toggle-open`);

      setProfile((prev) => ({
        ...prev,
        is_open: res.data.is_open
      }));
    } catch (err) {
      console.log(err);
      alert("Failed to change shop status. Check connection.");
    } finally {
      setDialogLoading(false); // 👈 Hide spinner
      setUpdating(false);
      setStatusDialog(false); // 👈 Close dialog only after API finishes
    }
  };

  // LOGOUT (Updated to show proper loading spinner)
  const handleLogout = async () => {
    try {
      setLogoutLoading(true); // 👈 Show spinner
      await logout();
    } catch (err) {
      console.log(err);
    } finally {
      setLogoutLoading(false);
      setLogoutDialog(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#FFFDF8"
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: '#F8C332' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pt: { xs: 1.5, sm: 3 }, pb: 10 }}>
      <Container maxWidth="sm">
        
        <Typography
          variant="h5" 
          fontWeight="900"
          sx={{ mb: 2, color: '#3A2C23', textAlign: 'center' }} 
        >
          Vendor Account
        </Typography>

        <Paper
          elevation={0}
          sx={{ 
            borderRadius: '20px', 
            p: { xs: 2, sm: 3 }, 
            bgcolor: '#FFFFFF',
            border: 'none',
            boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          {/* HEADER */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <Avatar 
              sx={{ 
                width: 48, 
                height: 48, 
                bgcolor: '#FFF0EE', 
                color: '#E85D3F' 
              }}
            >
              <StorefrontIcon fontSize="medium" />
            </Avatar>

            <Box>
              <Typography variant="h6" fontWeight="900" sx={{ color: '#3A2C23', lineHeight: 1.1, fontSize: '1.1rem' }}>
                {profile?.name || "Vendor"}
              </Typography>
              <Typography variant="caption" fontWeight="700" sx={{ color: '#A59D96', fontSize: '0.75rem' }}>
                Vendor Profile
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 1.5, borderStyle: 'dashed', borderColor: '#ECE5DA' }} />

          {/* EMAIL */}
          <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
            <Avatar sx={{ bgcolor: '#FFF6DE', color: '#F29D17', width: 36, height: 36 }}>
              <EmailIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                EMAIL
              </Typography>
              <Typography variant="body2" sx={{ color: '#2F2A25', fontWeight: '800', lineHeight: 1.1, fontSize: '0.9rem' }}>
                {profile?.email || "-"}
              </Typography>
            </Box>
          </Stack>

          {/* LOCATION */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: '#E1EED3', color: '#577A34', width: 36, height: 36 }}>
              <LocationOnIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                LOCATION
              </Typography>
              <Typography variant="body2" sx={{ color: '#2F2A25', fontWeight: '800', lineHeight: 1.1, fontSize: '0.9rem' }}>
                {profile?.location || "-"}
              </Typography>
            </Box>
          </Stack>

          {/* SERVICE AREA */}
          <Box sx={{ bgcolor: '#FFFDF8', p: 1.5, borderRadius: '12px', border: '1px solid #ECE5DA', mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: 'block', mb: 1, fontSize: '0.65rem' }}>
              SERVICE AREA
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={profile?.city || "City"}
                size="small"
                sx={{ bgcolor: '#F8C332', color: '#3A2C23', fontWeight: 800, borderRadius: '6px', fontSize: '0.75rem' }}
              />
              <Chip
                label={profile?.zone || "Zone"}
                size="small"
                sx={{ bgcolor: '#FFF0EE', color: '#E85D3F', fontWeight: 800, borderRadius: '6px', fontSize: '0.75rem' }}
              />
            </Stack>
          </Box>

          {/* SHOP STATUS */}
          <Box sx={{ bgcolor: '#FFFDF8', p: 1.5, borderRadius: '12px', border: '1px solid #ECE5DA', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: 'block', mb: 0.5, fontSize: '0.65rem' }}>
                KITCHEN STATUS
              </Typography>
              <Chip
                label={profile?.is_open ? "OPEN" : "CLOSED"}
                color={profile?.is_open ? "success" : "error"}
                size="small"
                sx={{ fontWeight: 800, borderRadius: '6px', fontSize: '0.75rem' }}
              />
            </Box>
            
            <Switch
              checked={profile?.is_open || false}
              onChange={() => setStatusDialog(true)}
              disabled={updating}
              color="success"
              size="small"
              sx={{ transform: 'scale(1.1)' }} 
            />
          </Box>

        </Paper>

        {/* LOGOUT BUTTON */}
        <Button
          fullWidth
          variant="contained"
          disableElevation
          startIcon={<LogoutIcon fontSize="small" />}
          onClick={() => setLogoutDialog(true)}
          sx={{
            mt: 2,
            py: 1.2,
            borderRadius: '16px',
            bgcolor: '#FFF0EE',
            color: '#E85D3F',
            fontWeight: '900',
            textTransform: 'none',
            fontSize: '1rem',
            border: '1px solid #FFE4E0',
            '&:hover': { bgcolor: '#FFE4E0' }
          }}
        >
          Logout
        </Button>

      </Container>

      {/* ================= NEW KITCHEN STATUS DIALOG ================= */}
      <Dialog
        open={statusDialog}
        // 👈 Prevent closing dialog by clicking outside if it is loading
        onClose={dialogLoading ? undefined : () => setStatusDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 1,
            bgcolor: '#FFFDF8',
            maxWidth: '320px',
            textAlign: 'center'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#3A2C23', pb: 1 }}>
          {profile?.is_open ? "Close Kitchen?" : "Open Kitchen?"}
        </DialogTitle>

        <DialogContent sx={{ pb: 2 }}>
          <DialogContentText sx={{ color: "#6E655E", fontWeight: 600, fontSize: '0.9rem' }}>
            Are you sure you want to {profile?.is_open ? "close" : "open"} your kitchen? 
            {profile?.is_open ? " Customers will not be able to place new orders." : " You will start receiving orders immediately."}
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 1.5 }}>
          <Button
            onClick={() => setStatusDialog(false)}
            variant="outlined"
            disabled={dialogLoading} // 👈 Disable while loading
            sx={{ fontWeight: 800, color: '#6E655E', borderColor: '#ECE5DA', textTransform: 'none', borderRadius: '12px', flex: 1 }}
          >
            Cancel
          </Button>

          <Button
            onClick={toggleShop}
            variant="contained"
            disableElevation
            disabled={dialogLoading} // 👈 Disable while loading
            sx={{ 
              fontWeight: 900, 
              borderRadius: '12px', 
              textTransform: 'none', 
              flex: 1,
              bgcolor: profile?.is_open ? '#DC2626' : '#577A34',
              color: '#FFFFFF',
              minHeight: '42px', // Prevent button resize when spinner is active
              '&:hover': { bgcolor: profile?.is_open ? '#B91C1C' : '#4d6b2f' },
              '&.Mui-disabled': { bgcolor: profile?.is_open ? '#fca5a5' : '#a3c286', color: '#fff' }
            }}
          >
            {/* 👈 Render proper loading spinner */}
            {dialogLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : (profile?.is_open ? "Close" : "Open")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= LOGOUT DIALOG ================= */}
      <Dialog
        open={logoutDialog}
        // 👈 Prevent closing dialog by clicking outside if it is loading
        onClose={logoutLoading ? undefined : () => setLogoutDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 1,
            bgcolor: '#FFFDF8',
            maxWidth: '320px',
            textAlign: 'center'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#3A2C23', pb: 1 }}>
          Logout
        </DialogTitle>

        <DialogContent sx={{ pb: 2 }}>
          <DialogContentText sx={{ color: "#6E655E", fontWeight: 600, fontSize: '0.9rem' }}>
            Are you sure you want to logout from your vendor account?
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 1.5 }}>
          <Button
            onClick={() => setLogoutDialog(false)}
            variant="outlined"
            disabled={logoutLoading} // 👈 Disable while loading
            sx={{ fontWeight: 800, color: '#6E655E', borderColor: '#ECE5DA', textTransform: 'none', borderRadius: '12px', flex: 1 }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleLogout}
            variant="contained"
            disableElevation
            disabled={logoutLoading} // 👈 Disable while loading
            sx={{ 
              fontWeight: 900, 
              borderRadius: '12px', 
              textTransform: 'none', 
              flex: 1,
              bgcolor: '#dc2626ef',
              color: '#FFFFFF',
              minHeight: '42px', // Prevent resize
              '&:hover': { bgcolor: '#B91C1C' },
              '&.Mui-disabled': { bgcolor: '#fca5a5', color: '#fff' }
            }}
          >
            {/* 👈 Render proper loading spinner */}
            {logoutLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : "Logout"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}